/**
 * ECharts Configuration Constants
 *
 * Theme colors and default configurations for ECharts components.
 * Uses GLZ Data360 brand colors for consistent visual identity.
 *
 * Brand Colors:
 * - Green:  #006842 (Primary - gains, long positions)
 * - Gold:   #BE9B53 (Accent - highlights, financing)
 * - Blue:   #91B6C0 (Info - net P&L, secondary data)
 * - Red:    #B85450 (Loss - losses, short positions)
 *
 * @module constants/echartsConfig
 */

// Brand colors for charts
const BRAND = {
  green: '#006842',
  greenLight: '#008B5A',
  gold: '#BE9B53',
  goldLight: '#D4B87A',
  blue: '#91B6C0',
  blueLight: '#B8D4DC',
  red: '#B85450',
  redLight: '#D4807C',
};

export const ECHARTS_COLORS = {
  // Exposure colors
  lmv: BRAND.green,           // Long Market Value - green (positive)
  smv: BRAND.red,             // Short Market Value - red (negative)
  
  // P&L colors
  netPnl: BRAND.blue,         // Net P&L - blue (informational)
  gross: BRAND.green,         // Gross P&L - green (primary)
  financing: BRAND.gold,      // Financing - gold (accent)
  rebates: BRAND.goldLight,   // Rebates - light gold
  
  // Primary series palette (for multi-series charts)
  primary: [
    BRAND.green,      // Primary green
    BRAND.gold,       // Gold accent
    BRAND.blue,       // Blue info
    '#7B9E89',        // Muted sage green
    '#D9C4A0',        // Muted gold
    '#A8C5CC',        // Light blue
    '#9E8B78',        // Muted brown
    '#8FADE8',        // Periwinkle
    '#B5A642',        // Olive
    '#7EA6A0',        // Teal
  ],
  
  // Semantic colors
  positive: BRAND.green,
  positiveLight: BRAND.greenLight,
  negative: BRAND.red,
  negativeLight: BRAND.redLight,
  neutral: BRAND.blue,
  highlight: BRAND.gold,
  
  // Gradients for area charts
  gradients: {
    green: [`rgba(0, 104, 66, 0.7)`, `rgba(0, 104, 66, 0.05)`],
    gold: [`rgba(190, 155, 83, 0.7)`, `rgba(190, 155, 83, 0.05)`],
    blue: [`rgba(145, 182, 192, 0.7)`, `rgba(145, 182, 192, 0.05)`],
    red: [`rgba(184, 84, 80, 0.7)`, `rgba(184, 84, 80, 0.05)`],
  },
};

export const ECHARTS_DEFAULTS = {
  animation: true,
  animationDuration: 500,
  animationEasing: 'cubicOut',
  grid: { top: 35, right: 15, bottom: 70, left: 0, containLabel: true },
  compactGrid: { top: 30, right: 15, bottom: 45, left: 0, containLabel: true },
  tooltip: {
    backgroundColor: 'rgba(255, 255, 255, 0.98)',
    borderColor: BRAND.green,
    borderWidth: 1,
    textStyle: { color: '#262626', fontSize: 12, fontWeight: 500 },
    extraCssText: 'box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12); border-radius: 8px;',
  },
  legend: {
    bottom: 5,
    left: 'center',
    textStyle: { fontSize: 11, color: '#525251' },
    itemGap: 15,
    itemWidth: 18,
    itemHeight: 10,
  },
  textStyle: { 
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    color: '#404040',
  },
  // Axis styling
  axisLine: {
    lineStyle: { color: '#D4D4D3' },
  },
  splitLine: {
    lineStyle: { color: '#EBEBEA', type: 'dashed' },
  },
};

export const EXPOSURE_ECHART_CONFIG = {
  grossExposure: {
    title: 'Gross Exposure by Strategy (Top 15)',
    subtitle: 'Gross Exposure = LMV + SMV (stacked bars)',
    xAxisKey: 'strategy',
    bars: [
      { dataKey: 'lmv', name: 'Long Market Value', color: ECHARTS_COLORS.lmv, stack: 'exposure' },
      { dataKey: 'smv', name: 'Short Market Value', color: ECHARTS_COLORS.smv, stack: 'exposure' },
    ],
    height: 360,
    xAxisRotate: 35,
  },
  dailyExposure: {
    title: 'Daily Exposure Trend',
    subtitle: 'Aggregated daily exposure over the selected period',
    xAxisKey: 'dateDisplay',
    bars: [
      { dataKey: 'lmv', name: 'Long Market Value', color: ECHARTS_COLORS.lmv, stack: 'exposure' },
      { dataKey: 'smv', name: 'Short Market Value', color: ECHARTS_COLORS.smv, stack: 'exposure' },
    ],
    height: 280,
    xAxisRotate: 30,
    compact: true,
  },
};

export const PNL_ECHART_CONFIG = {
  dailyPnL: {
    title: 'Daily P&L Movements',
    subtitle: 'Net P&L by day (MTD)',
    xAxisKey: 'dateDisplay',
    barColor: ECHARTS_COLORS.primary[0],
    lineColor: ECHARTS_COLORS.primary[1],
    height: 300,
  },
  cumulativePnL: {
    title: 'Cumulative P&L',
    subtitle: 'Running total over time',
    xAxisKey: 'dateDisplay',
    areaColor: ECHARTS_COLORS.primary[0],
    gradientColors: ECHARTS_COLORS.gradients.green,
    height: 300,
  },
};

const echartsConfig = { ECHARTS_COLORS, ECHARTS_DEFAULTS, EXPOSURE_ECHART_CONFIG, PNL_ECHART_CONFIG };
export default echartsConfig;
